import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import { formatMoney } from "@finora/config";
import { EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  SUCCESS: "success",
  FAILED: "warning",
  PENDING: "neutral",
};

const STATUS_LABEL: Record<string, string> = {
  SUCCESS: "Uspešno",
  FAILED: "Neuspešno",
  PENDING: "U toku",
};

export function ActivityLogTab() {
  const { data: entries, isLoading } = useQuery({
    queryKey: ["cloud-api-activity-log"],
    queryFn: () => api.getCloudApiActivityLog(),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Poslednjih 10 pokušaja fiskalizacije preko partnerskog API-ja (npr. WooCommerce), preko svih
        integracija. Osvežava se automatski.
      </p>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : !entries || entries.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={ScrollText}
              title="Još nema pokušaja fiskalizacije"
              description="Čim partner (npr. WooCommerce) pošalje prvi zahtev na /v1/fiscalize, pojaviće se ovde."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Vreme</th>
                <th>Integracija</th>
                <th>External ID</th>
                <th>Iznos</th>
                <th>Status</th>
                <th>Fiskalni broj / greška</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="text-slate-500">{new Date(entry.createdAt).toLocaleString("sr-RS")}</td>
                  <td className="text-slate-700">{entry.integrationName ?? "—"}</td>
                  <td className="font-mono text-xs text-slate-600">{entry.externalId ?? "—"}</td>
                  <td className="text-slate-700">{formatMoney(entry.grossAmount)}</td>
                  <td>
                    <StatusBadge
                      label={STATUS_LABEL[entry.status] ?? entry.status}
                      tone={STATUS_TONE[entry.status] ?? "neutral"}
                    />
                  </td>
                  <td className="max-w-xs text-xs text-slate-500">
                    {entry.status === "SUCCESS" ? (
                      <span className="font-mono">{entry.fiscalNumber ?? "—"}</span>
                    ) : (
                      <span className="text-red-600">{entry.providerMessage ?? entry.technicalDetail ?? "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
